import { useState, useEffect } from 'react';

/**
 * Custom hook for counting animation
 * @param end - The target number to count up to
 * @param duration - Animation duration in milliseconds (default: 1000ms)
 * @param start - Starting number (default: 0)
 * @param easing - Easing function type ('linear' | 'easeOut' | 'easeIn' | 'easeInOut')
 * @returns Object with current count and animation state
 */
export const useCountUp = (
  end: number, 
  duration: number = 1000, 
  start: number = 0,
  easing: 'linear' | 'easeOut' | 'easeIn' | 'easeInOut' = 'easeOut'
) => {
  const [count, setCount] = useState(start);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (end === start) {
      setCount(start);
      return;
    }

    setIsAnimating(true);
    setCount(start);

    const startTime = Date.now();
    const startValue = start;
    const endValue = end;

    // Easing functions
    const easingFunctions = {
      linear: (t: number) => t,
      easeOut: (t: number) => 1 - Math.pow(1 - t, 3), // Cubic ease out
      easeIn: (t: number) => Math.pow(t, 3), // Cubic ease in
      easeInOut: (t: number) => t < 0.5 ? 2 * Math.pow(t, 2) : 1 - Math.pow(-2 * t + 2, 2) / 2
    };

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      const easeFunction = easingFunctions[easing];
      const easedProgress = easeFunction(progress);
      const currentValue = Math.floor(startValue + (endValue - startValue) * easedProgress);
      
      setCount(currentValue);

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        setCount(endValue);
        setIsAnimating(false);
      }
    };

    requestAnimationFrame(animate);
  }, [end, duration, start, easing]);

  return { count, isAnimating };
};

/**
 * Predefined animation configurations for common use cases
 * These are factory functions that return the hook configuration
 */
export const CountUpPresets = {
  // Quick animation for small numbers
  quick: (end: number) => ({ end, duration: 600, start: 0, easing: 'easeOut' as const }),
  
  // Standard animation for medium numbers
  standard: (end: number) => ({ end, duration: 1000, start: 0, easing: 'easeOut' as const }),
  
  // Slow animation for large numbers or emphasis
  slow: (end: number) => ({ end, duration: 1500, start: 0, easing: 'easeOut' as const }),
  
  // Bounce effect for important numbers
  bounce: (end: number) => ({ end, duration: 1200, start: 0, easing: 'easeInOut' as const }),
  
  // Linear for precise counting
  linear: (end: number) => ({ end, duration: 1000, start: 0, easing: 'linear' as const })
};

export default useCountUp;
